import { maskitoInitialCalibrationPlugin, maskitoTransform } from '@maskito/core'
import type { MaskitoNumberParams } from '@maskito/kit'
import { maskitoNumberOptionsGenerator } from '@maskito/kit'
import { useMaskito } from '@maskito/react'
import { forwardRef, useCallback, useEffect, useRef, useMemo } from 'react'

import { TextField, WCTextField } from '@loadsmart/miranda-react'
import type { TextFieldProps } from '@loadsmart/miranda-react'

import { mergeRefs } from '../../utils/ref'

export type CurrencyMask = MaskitoNumberParams

export const defaultCurrencyMask: Partial<MaskitoNumberParams> = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  min: 0,
  prefix: '$',
}

export type CurrencyFieldProps = Readonly<
  Omit<TextFieldProps, 'value' | 'ref'> & {
    /** Maskito still formats strings; pass a number so the first paint matches the mask (e.g. RHF number fields). */
    value: string | number | null
    currencyMask?: CurrencyMask
    /** Passed through to Miranda `TextField` (not always present on `TextFieldProps`). */
    'data-testid'?: string
  }
>

function isMirandaTextFieldHost(host: HTMLElement): host is WCTextField {
  return host instanceof WCTextField
}

export const CurrencyField = forwardRef<WCTextField, CurrencyFieldProps>(function CurrencyField(
  { value, currencyMask = defaultCurrencyMask, ...textFieldProps },
  forwardedRef
) {
  const maskOptions = useMemo(() => getMaskOptions(currencyMask), [currencyMask])

  // Miranda's TextField WC is not a controlled component — it ignores React `value`
  // prop updates after mount. We need a ref to the host element to imperatively
  // sync the shadow input when `displayValue` changes (e.g. cross-field calculations).
  const hostRef = useRef<WCTextField | null>(null)

  const maskitoRef = useMaskito({
    options: maskOptions,
    elementPredicate: getElementPredicate,
  })

  const maskitoHostRef = useCallback(
    (el: WCTextField | null) => {
      maskitoRef(el)
    },
    [maskitoRef]
  )

  const setRef = useMemo(
    () => mergeRefs<WCTextField | null>(hostRef, maskitoHostRef, forwardedRef),
    [forwardedRef, maskitoHostRef]
  )

  const displayValue = useMemo(() => {
    return maskitoTransform(String(value) || '', maskOptions)
  }, [value, maskOptions])

  // Imperatively write to the shadow input when the value changes programmatically.
  // No event dispatched — avoids triggering onChange and creating an update loop.
  // The guard skips the write when Maskito already applied the same value (user typing).
  useEffect(() => {
    let mounted = true
    const host = hostRef.current
    if (!host) return
    getElementPredicate(host).then(input => {
      if (mounted && input.value !== displayValue) {
        input.value = displayValue
      }
    })
    return () => {
      mounted = false
    }
  }, [displayValue])

  return (
    <TextField
      ref={setRef}
      {...textFieldProps}
      data-testid={textFieldProps['data-testid'] ?? 'm-currency-input'}
      placeholder={textFieldProps.placeholder ?? '$'}
      value={displayValue}
    />
  )
})

CurrencyField.displayName = 'CurrencyField'

export function getMaskOptions(currencyMask: CurrencyMask) {
  const options = maskitoNumberOptionsGenerator(currencyMask)
  const plugins = [maskitoInitialCalibrationPlugin(options)]
  return {
    ...options,
    plugins,
  }
}

export async function getElementPredicate(host: HTMLElement) {
  if (!isMirandaTextFieldHost(host)) {
    throw new Error('CurrencyField: Maskito host must be Miranda TextField')
  }
  await host.updateComplete
  const input = host.input
  if (input == null) {
    throw new Error('CurrencyField: Miranda TextField has no input element')
  }
  return input
}
